import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { SubscriptionProvider } from "@/hooks/useSubscription";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { AdminRoute } from "@/components/AdminRoute";
import { SubscriptionGuard } from "@/components/subscription/SubscriptionGuard";
import { LandingPage } from "@/pages/LandingPage";
import { Suspense, lazy, useEffect } from "react";
import { PageLoader } from "@/components/PageLoader";

const AuthPage = lazy(() => import("@/pages/AuthPage").then(m => ({ default: m.AuthPage })));
const ForgotPasswordPage = lazy(() => import("@/pages/ForgotPasswordPage").then(m => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage").then(m => ({ default: m.ResetPasswordPage })));
const PublicProfilePage = lazy(() => import("./pages/PublicProfilePage").then(m => ({ default: m.PublicProfilePage })));
const EntityPage = lazy(() => import("./pages/EntityPage").then(m => ({ default: m.EntityPage })));
const AppLayout = lazy(() => import("@/components/layout/AppLayout").then(m => ({ default: m.AppLayout })));

const HomePage = lazy(() => import("@/pages/HomePage").then(m => ({ default: m.HomePage })));
const StudyPage = lazy(() => import("@/pages/StudyPage").then(m => ({ default: m.StudyPage })));
const SavedItemsPage = lazy(() => import("@/pages/SavedItemsPage").then(m => ({ default: m.SavedItemsPage })));
const RankingPage = lazy(() => import("@/pages/RankingPage").then(m => ({ default: m.RankingPage })));
const CompetePage = lazy(() => import("@/pages/CompetePage").then(m => ({ default: m.CompetePage })));
const PerformancePage = lazy(() => import("@/pages/PerformancePage").then(m => ({ default: m.PerformancePage })));
const ProfilePage = lazy(() => import("@/pages/ProfilePage").then(m => ({ default: m.ProfilePage })));
const AdminPage = lazy(() => import("@/pages/AdminPage").then(m => ({ default: m.AdminPage })));
const BlogPage = lazy(() => import("@/pages/BlogPage").then(m => ({ default: m.BlogPage })));
const BlogPostDetail = lazy(() => import("@/pages/BlogPostDetail").then(m => ({ default: m.BlogPostDetail })));
const FlashcardSessionPage = lazy(() => import("@/pages/FlashcardSessionPage").then(m => ({ default: m.FlashcardSessionPage })));
const VideoPlayerPage = lazy(() => import("@/pages/VideoPlayerPage").then(m => ({ default: m.VideoPlayerPage })));
const VideosPage = lazy(() => import("@/pages/VideosPage").then(m => ({ default: m.VideosPage })));
const SimuladorSessionPage = lazy(() => import("@/pages/SimuladorSessionPage").then(m => ({ default: m.SimuladorSessionPage })));
const EnarmSelectaPage = lazy(() => import("@/pages/EnarmSelectaPage").then(m => ({ default: m.EnarmSelectaPage })));
const AffiliatesPage = lazy(() => import("@/pages/AffiliatesPage").then(m => ({ default: m.AffiliatesPage })));
const SettingsPage = lazy(() => import("@/pages/SettingsPage").then(m => ({ default: m.SettingsPage })));
const HelpPage = lazy(() => import("@/pages/HelpPage").then(m => ({ default: m.HelpPage })));
const SubscriptionPlanPage = lazy(() => import("@/pages/SubscriptionPlanPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { FeatureFlagRoute } from "@/components/FeatureFlagRoute";
import { captureReferralCodeFromUrl, claimStoredReferralCode } from "@/lib/affiliates";
import { useAuth } from "@/hooks/useAuth";
const ForumPage = lazy(() => import("@/pages/Forum/ForumPage").then(m => ({ default: m.ForumPage })));
const ForumCategoryPage = lazy(() => import("@/pages/Forum/ForumCategoryPage").then(m => ({ default: m.ForumCategoryPage })));
const ForumTopicPage = lazy(() => import("@/pages/Forum/ForumTopicPage").then(m => ({ default: m.ForumTopicPage })));
const MaintenancePage = lazy(() => import("@/pages/MaintenancePage"));
const StatusPage = lazy(() => import("@/pages/StatusPage"));
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { ConfirmDialogProvider } from "@/components/ui/confirm-dialog";
import { GoogleAnalytics } from "@/components/GoogleAnalytics";
import { FacebookPixel } from "@/components/FacebookPixel";
const ComingSoonPage = lazy(() => import("@/pages/ComingSoonPage").then(m => ({ default: m.ComingSoonPage })));
import { useComingSoon } from "@/hooks/useComingSoon";
const EnarmInteractiveArticlePage = lazy(() => import("@/pages/EnarmInteractiveArticlePage").then(m => ({ default: m.EnarmInteractiveArticlePage })));
const TerminosPage = lazy(() => import("@/pages/legal/TerminosPage"));
const PrivacidadPage = lazy(() => import("@/pages/legal/PrivacidadPage"));
const CookiesPage = lazy(() => import("@/pages/legal/CookiesPage"));
const AvisoMedicoPage = lazy(() => import("@/pages/legal/AvisoMedicoPage"));
const ReembolsosPage = lazy(() => import("@/pages/legal/ReembolsosPage"));

const legalRoutes = (
  <>
    <Route path="/terminos" element={<Suspense fallback={<PageLoader />}><TerminosPage /></Suspense>} />
    <Route path="/privacidad" element={<Suspense fallback={<PageLoader />}><PrivacidadPage /></Suspense>} />
    <Route path="/cookies" element={<Suspense fallback={<PageLoader />}><CookiesPage /></Suspense>} />
    <Route path="/aviso-medico" element={<Suspense fallback={<PageLoader />}><AvisoMedicoPage /></Suspense>} />
    <Route path="/reembolsos" element={<Suspense fallback={<PageLoader />}><ReembolsosPage /></Suspense>} />
  </>
);

const selectaDevelopmentPreview = import.meta.env.DEV ? (
  <Route
    path="/__preview/enarm-selecta"
    element={<div className="min-h-screen bg-background"><Suspense fallback={<PageLoader />}><EnarmSelectaPage /></Suspense></div>}
  />
) : null;

const queryClient = new QueryClient();

function RegistroRedirect() {
  const location = useLocation();
  return <Navigate to={`/auth${location.search}`} replace />;
}

function PublicRoutes() {
  const { comingSoon, loading } = useComingSoon();

  if (loading) return <PageLoader />;

  if (comingSoon) {
    return (
      <Routes>
        {selectaDevelopmentPreview}
        <Route path="/" element={<Suspense fallback={<PageLoader />}><ComingSoonPage /></Suspense>} />
        <Route path="/blog" element={<Suspense fallback={<PageLoader />}><BlogPage /></Suspense>} />
        <Route path="/blog/enarm-2025-interactivo" element={<Suspense fallback={<PageLoader />}><EnarmInteractiveArticlePage /></Suspense>} />
        <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><BlogPostDetail /></Suspense>} />
        <Route path="/admin/*" element={
          <AdminRoute>
            <Suspense fallback={<PageLoader />}><AdminPage /></Suspense>
          </AdminRoute>
        } />
        <Route path="/mantenimiento" element={<Suspense fallback={<PageLoader />}><MaintenancePage /></Suspense>} />
        {legalRoutes}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {selectaDevelopmentPreview}
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<Suspense fallback={<PageLoader />}><AuthPage /></Suspense>} />
      <Route path="/registro" element={<RegistroRedirect />} />
      <Route path="/register" element={<RegistroRedirect />} />
      <Route path="/signup" element={<RegistroRedirect />} />
      <Route path="/unete" element={<RegistroRedirect />} />
      <Route path="/invitacion" element={<RegistroRedirect />} />
      <Route path="/p/:username" element={<Suspense fallback={<PageLoader />}><FeatureFlagRoute flag="social" redirectTo="/"><PublicProfilePage /></FeatureFlagRoute></Suspense>} />
      <Route path="/forgot-password" element={<Suspense fallback={<PageLoader />}><ForgotPasswordPage /></Suspense>} />
      <Route path="/reset-password" element={<Suspense fallback={<PageLoader />}><ResetPasswordPage /></Suspense>} />
      <Route path="/blog" element={<Suspense fallback={<PageLoader />}><BlogPage /></Suspense>} />
      <Route path="/blog/enarm-2025-interactivo" element={<Suspense fallback={<PageLoader />}><EnarmInteractiveArticlePage /></Suspense>} />
      <Route path="/blog/:slug" element={<Suspense fallback={<PageLoader />}><BlogPostDetail /></Suspense>} />
      <Route path="/mantenimiento" element={<Suspense fallback={<PageLoader />}><MaintenancePage /></Suspense>} />
      <Route path="/status" element={<Suspense fallback={<PageLoader />}><StatusPage /></Suspense>} />
      {legalRoutes}
      <Route path="/planes" element={
        <ProtectedRoute>
          <Suspense fallback={<PageLoader />}><SubscriptionPlanPage /></Suspense>
        </ProtectedRoute>
      } />
      <Route path="/admin/*" element={
        <AdminRoute>
          <Suspense fallback={<PageLoader />}><AdminPage /></Suspense>
        </AdminRoute>
      } />
      <Route path="/app/*" element={<ProtectedRoute><AppRoutes /></ProtectedRoute>} />
      <Route path="*" element={<Suspense fallback={<PageLoader />}><NotFound /></Suspense>} />
    </Routes>
  );
}



function AppRoutes() {
  return (
    <ProtectedRoute>
      <Suspense fallback={<PageLoader />}>
        <AppLayout>
          <Routes>
            {/* Study sessions */}
            <Route path="estudia/flashcards" element={<SubscriptionGuard><FlashcardSessionPage /></SubscriptionGuard>} />
            <Route path="estudia/flashcards/:subtopicId" element={<SubscriptionGuard><FlashcardSessionPage /></SubscriptionGuard>} />
            <Route path="estudia/simulador/:subtopicId" element={<SubscriptionGuard><SimuladorSessionPage /></SubscriptionGuard>} />

            {/* Normal routes */}
            <Route path="" element={<HomePage />} />
            <Route path="estudia" element={<SubscriptionGuard><StudyPage /></SubscriptionGuard>} />
            <Route path="estudia/selecta" element={<FeatureFlagRoute flag="enarm_selecta_2026" redirectTo="/app/estudia"><SubscriptionGuard><EnarmSelectaPage /></SubscriptionGuard></FeatureFlagRoute>} />
            <Route path="estudia/guardados" element={<SubscriptionGuard><SavedItemsPage /></SubscriptionGuard>} />
            <Route path="estudia/area/:areaId" element={<SubscriptionGuard><StudyPage /></SubscriptionGuard>} />
            <Route path="estudia/area/:areaId/especialidad/:specialtyId" element={<SubscriptionGuard><StudyPage /></SubscriptionGuard>} />
            <Route path="estudia/video/:subtopicId" element={<SubscriptionGuard><VideoPlayerPage /></SubscriptionGuard>} />
            <Route path="compite" element={<SubscriptionGuard><CompetePage /></SubscriptionGuard>} />
            <Route path="ranking" element={<FeatureFlagRoute flag="social"><FeatureFlagRoute flag="ranking"><SubscriptionGuard><RankingPage /></SubscriptionGuard></FeatureFlagRoute></FeatureFlagRoute>} />
            <Route path="ranking/:type/:id" element={<FeatureFlagRoute flag="social"><FeatureFlagRoute flag="ranking"><SubscriptionGuard><EntityPage /></SubscriptionGuard></FeatureFlagRoute></FeatureFlagRoute>} />

            <Route path="desempeno" element={<SubscriptionGuard><PerformancePage /></SubscriptionGuard>} />
            <Route path="videos" element={<SubscriptionGuard><VideosPage /></SubscriptionGuard>} />
            <Route path="perfil" element={<FeatureFlagRoute flag="social" redirectTo="/app/configuracion"><ProfilePage /></FeatureFlagRoute>} />
            <Route path="configuracion" element={<SettingsPage />} />
            <Route path="afiliados" element={<AffiliatesPage />} />

            {/* <Route path="/ayuda" element={<HelpPage />} /> */}
            <Route path="/foro" element={<FeatureFlagRoute flag="forum"><ForumPage /></FeatureFlagRoute>} />
            <Route path="/foro/categoria/:categoryId" element={<FeatureFlagRoute flag="forum"><ForumCategoryPage /></FeatureFlagRoute>} />
            <Route path="/foro/tema/:topicId" element={<FeatureFlagRoute flag="forum"><ForumTopicPage /></FeatureFlagRoute>} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppLayout>
      </Suspense>
    </ProtectedRoute>
  );
}

function ReferralTracker() {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    captureReferralCodeFromUrl(location.search);
  }, [location.search]);

  useEffect(() => {
    if (user) {
      claimStoredReferralCode();
    }
  }, [user]);

  return null;
}

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
      <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme-v2">
      <AuthProvider>
        <SubscriptionProvider>
          <TooltipProvider>
            <ConfirmDialogProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <GoogleAnalytics />
              <FacebookPixel />
              <AnnouncementBanner />
              <ReferralTracker />
              <PublicRoutes />

            </BrowserRouter>
            </ConfirmDialogProvider>
            </TooltipProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
      </HelmetProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
